import React, { useState, useMemo } from "react";
import { AUTH_ACTION } from "../../redux/action";
import { Toaster, encryptPassword } from "../../utils/common";
import "./changePasswordModal.css";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";

const ChangePasswordModal = ({ open, onClose }) => {
  const [inputs, setInputs] = useState({
    currentPassword: {
      type: "text",
      labelname: "Current Password",
      value: "",
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
      password: true,
      showPassword: false,
    },
    newPassword: {
      type: "text",
      labelname: "New Password",
      value: "",
      validation: [
        { name: "required" },
        { name: "minLength", value: 8 },
        { name: "pattern", regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/, errorMsg: "Password must include at least one uppercase letter, one lowercase letter, one number, and one special character." },
      ],
      error: null,
      errmsg: null,
      password: true,
      showPassword: false,
    },
    confirmPassword: {
      type: "text",
      labelname: "Confirm New Password",
      value: "",
      validation: [
        { name: "required" },
        { name: "comparePassword", compareText: "" },
      ],
      error: null,
      errmsg: null,
      password: true,
      showPassword: false,
    },
  });

  // Password requirement checks
  const passwordRequirements = useMemo(() => {
    const password = inputs.newPassword.value;
    return {
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasMinLength: password.length >= 8,
    };
  }, [inputs.newPassword.value]);

  const handleChange = (value, fieldKey) => {
    if (fieldKey === "confirmPassword") {
      inputs[fieldKey].validation[1].compareText = inputs.newPassword.value;
    }

    const errorcheck = checkValidation(
      value,
      inputs[fieldKey].validation
    );
    inputs[fieldKey].value = value;
    inputs[fieldKey].error = !errorcheck.state;
    inputs[fieldKey].errmsg = errorcheck.msg;
    setInputs({ ...inputs });
  };

  const togglePasswordVisibility = (fieldKey) => {
    inputs[fieldKey].showPassword = !inputs[fieldKey].showPassword;
    inputs[fieldKey].type = inputs[fieldKey].showPassword ? "text" : "password";
    setInputs({ ...inputs });
  };

  const checkValidation = (value, rules) => {
    for (let rule of rules) {
      if (rule.name === "required" && !value.trim()) {
        return { state: false, msg: "This field is required" };
      }
      if (rule.name === "minLength" && value.length < rule.value) {
        return { state: false, msg: `Minimum ${rule.value} characters required` };
      }
      if (rule.name === "pattern" && !rule.regex.test(value)) {
        return { state: false, msg: rule.errorMsg };
      }
      if (
        rule.name === "comparePassword" &&
        value !== rule.compareText
      ) {
        return { state: false, msg: "Passwords do not match" };
      }
    }
    return { state: true, msg: "" };
  };

  const handleClose = () => {
    // Reset form when closing
    setInputs({
      currentPassword: {
        ...inputs.currentPassword,
        value: "",
        error: null,
        errmsg: null,
        showPassword: false,
      },
      newPassword: {
        ...inputs.newPassword,
        value: "",
        error: null,
        errmsg: null,
        showPassword: false,
      },
      confirmPassword: {
        ...inputs.confirmPassword,
        value: "",
        error: null,
        errmsg: null,
        showPassword: false,
      },
    });
    onClose();
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      const errorList = Object.keys(inputs).filter((fieldKey) => {
        const errorcheck = checkValidation(
          inputs[fieldKey].value,
          inputs[fieldKey].validation
        );
        inputs[fieldKey].error = !errorcheck.state;
        inputs[fieldKey].errmsg = errorcheck.msg;
        return !errorcheck.state;
      });
      setInputs({ ...inputs });
      if (!errorList.length) {
        const email = localStorage.getItem("email");
        if (!email) {
          Toaster.error("Email not found. Please login again.");
          return;
        }
        
        const status = await AUTH_ACTION.authService(
          {
            old_password: encryptPassword(inputs.currentPassword.value),
            new_password: encryptPassword(inputs.newPassword.value),
            email: email,
          },
          "newAuth"
        );
        
        if (status === "Success") {
          Toaster.success("Password has been changed successfully");
          handleClose();
        } else if (typeof status === "string" && status !== "Success") {
          Toaster.error(status);
        } else if (status && status.message) {
          Toaster.error(status.message);
        } else {
          Toaster.error("An error occurred. Please try again.");
        }
      }
    } catch (error) {
      console.error("Change password error:", error);
      Toaster.error(error?.response?.data?.message || error?.message || "An error occurred. Please try again.");
    }
  };

  if (!open) return null;

  return (
    <div className="change-password-modal-overlay" onClick={handleClose}>
      <div className="change-password-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="change-password-modal-title">Change Password</h2>
        <p className="change-password-modal-subtitle">
          Update password for account security
        </p>

        <form onSubmit={handleSubmit} className="change-password-form">
          <div className="change-password-input-wrapper">
            <div className="change-password-input-group">
              <label className="change-password-label">
                {inputs.currentPassword.labelname}
              </label>
              <div className="change-password-input-container">
                <input
                  type={inputs.currentPassword.showPassword ? "text" : "password"}
                  className="change-password-input"
                  value={inputs.currentPassword.value}
                  onChange={(e) => handleChange(e.target.value, "currentPassword")}
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  className="change-password-eye-icon"
                  onClick={() => togglePasswordVisibility("currentPassword")}
                >
                  {inputs.currentPassword.showPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </button>
              </div>
              {inputs.currentPassword.error && (
                <span className="change-password-error">
                  {inputs.currentPassword.errmsg}
                </span>
              )}
              <div className="change-password-forgot-link">
                <a href="/forget_password" className="forgot-password-link">
                  Forgot Password?
                </a>
              </div>
            </div>

            <div className="change-password-input-group">
              <label className="change-password-label">
                {inputs.newPassword.labelname}
              </label>
              <div className="change-password-input-container">
                <input
                  type={inputs.newPassword.showPassword ? "text" : "password"}
                  className="change-password-input"
                  value={inputs.newPassword.value}
                  onChange={(e) => handleChange(e.target.value, "newPassword")}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  className="change-password-eye-icon"
                  onClick={() => togglePasswordVisibility("newPassword")}
                >
                  {inputs.newPassword.showPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </button>
              </div>
              {inputs.newPassword.error && (
                <span className="change-password-error">
                  {inputs.newPassword.errmsg}
                </span>
              )}
            </div>

            <div className="change-password-input-group">
              <label className="change-password-label">
                {inputs.confirmPassword.labelname}
              </label>
              <div className="change-password-input-container">
                <input
                  type={inputs.confirmPassword.showPassword ? "text" : "password"}
                  className="change-password-input"
                  value={inputs.confirmPassword.value}
                  onChange={(e) => handleChange(e.target.value, "confirmPassword")}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  className="change-password-eye-icon"
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                >
                  {inputs.confirmPassword.showPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </button>
              </div>
              {inputs.confirmPassword.error && (
                <span className="change-password-error">
                  {inputs.confirmPassword.errmsg}
                </span>
              )}
            </div>
          </div>

          {/* Password Requirements */}
          <div className="change-password-requirements">
            <h3 className="change-password-requirements-title">Must Contain</h3>
            <div className="change-password-requirements-list">
              <div
                className={`change-password-requirement-item ${
                  passwordRequirements.hasUppercase ? "met" : ""
                }`}
              >
                {passwordRequirements.hasUppercase ? (
                  <CheckCircleIcon className="requirement-icon met" />
                ) : (
                  <CancelIcon className="requirement-icon" />
                )}
                <span>At least 1 uppercase</span>
              </div>
              <div
                className={`change-password-requirement-item ${
                  passwordRequirements.hasNumber ? "met" : ""
                }`}
              >
                {passwordRequirements.hasNumber ? (
                  <CheckCircleIcon className="requirement-icon met" />
                ) : (
                  <CancelIcon className="requirement-icon" />
                )}
                <span>At least 1 number</span>
              </div>
              <div
                className={`change-password-requirement-item ${
                  passwordRequirements.hasMinLength ? "met" : ""
                }`}
              >
                {passwordRequirements.hasMinLength ? (
                  <CheckCircleIcon className="requirement-icon met" />
                ) : (
                  <CancelIcon className="requirement-icon" />
                )}
                <span>At least 8 character</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="change-password-modal-buttons">
            <button
              type="button"
              className="change-password-modal-btn change-password-modal-btn-cancel"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="change-password-modal-btn change-password-modal-btn-submit"
            >
              Change Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;

